/**
 * Code-based TanStack Router route tree for the platform console.
 *
 * Routes (clean, notification-deep-link-friendly URLs):
 *   /login                 — email/password sign-in (+ MFA gate)
 *   /tenants               — tenant list           (authenticated)
 *   /tenants/new           — create tenant         (authenticated)
 *   /tenants/$id           — tenant detail/edit    (authenticated, deep-linkable)
 *
 * All four routes are flat children of the root so the URLs stay clean. The
 * auth/MFA gating depends on React context (AuthProvider), so it is enforced in
 * the component tree rather than in `beforeLoad`: the root component renders the
 * bare <Outlet> for /login and wraps every other route in <AuthGate><AppShell>.
 */

import React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router';
import { AuthGate } from './auth/AuthGate.js';
import { LoginScreen } from './auth/LoginScreen.js';
import { AppShell } from './components/AppShell.js';
import { ObradorsList } from './features/obradors/ObradorsList.js';
import { ObradorCreate } from './features/obradors/ObradorCreate.js';
import { ObradorDetail } from './features/obradors/ObradorDetail.js';

/**
 * Root layout: /login renders bare; every other route is gated and framed by the
 * authenticated AppShell. AppShell hosts its own <Outlet> for the matched screen.
 */
function RootLayout(): React.ReactElement {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname === '/login') {
    return <Outlet />;
  }
  return (
    <AuthGate>
      <AppShell />
    </AuthGate>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tenants' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

const obradorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants',
  component: ObradorsList,
});

const obradorCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/new',
  component: ObradorCreate,
});

const obradorDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$id',
  component: ObradorDetail,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  obradorsRoute,
  obradorCreateRoute,
  obradorDetailRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
